import { Component, ErrorInfo, ReactNode } from 'react'
import { isChunkLoadError } from '../utils/chunkLoadError'

interface SafeSubtreeProps {
  children: ReactNode
  /** Rendered when a child throws. Defaults to nothing (null). */
  fallback?: ReactNode
  /**
   * When any value in this array changes (compared with Object.is), the boundary
   * clears a caught error and re-renders its children. Use it to auto-retry once
   * the underlying state a subtree depends on becomes ready.
   */
  resetKeys?: readonly unknown[]
  /** Optional label included in the console warning when an error is caught. */
  label?: string
}

interface SafeSubtreeState {
  hasError: boolean
  error: Error | null
}

/**
 * Minimal error boundary that fails a subtree *silently* — it renders `fallback`
 * (default: nothing) instead of letting a throw bubble to the app-level boundary
 * and blank the whole UI — and automatically retries when any `resetKeys` value
 * changes.
 *
 * Used to safely mount UI that may render before its underlying state is ready.
 * For example, the in-room bottom bar now mounts at `roomJoined` (so its lazy
 * chunk downloads during the loading phase) while the Phaser scene is still
 * loading; a transient "not ready" throw is swallowed here and the subtree
 * re-renders once loading completes (its `resetKeys` change), rather than
 * crashing the app.
 *
 * This intentionally does NOT render a visible error/retry UI (unlike
 * LiveKitErrorBoundary) — the wrapped UI is non-critical chrome that should just
 * appear when it can.
 */
export default class SafeSubtree extends Component<SafeSubtreeProps, SafeSubtreeState> {
  state: SafeSubtreeState = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): SafeSubtreeState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Chunk/dynamic-import failures are NOT ours to swallow — render() rethrows them so the
    // app-level AppErrorBoundary can run its full-reload recovery. Don't log them as handled here.
    if (isChunkLoadError(error)) return
    const tag = this.props.label ? ` [${this.props.label}]` : ''
    console.warn(`[SafeSubtree]${tag} caught an error; rendering fallback and will retry on reset:`, error, info)
  }

  componentDidUpdate(prevProps: SafeSubtreeProps): void {
    if (this.state.hasError && resetKeysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.setState({ hasError: false, error: null })
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // A stale-chunk / failed dynamic-import must reach the app-level AppErrorBoundary, which
      // reloads the page to recover from a redeploy. Swallowing it here would leave the subtree
      // permanently empty: React.lazy caches the rejection, so an auto-retry on resetKeys would just
      // re-throw the same cached error. Only transient "not ready yet" errors are swallowed. [SC-247]
      if (isChunkLoadError(this.state.error)) throw this.state.error
      return this.props.fallback ?? null
    }
    return this.props.children
  }
}

function resetKeysChanged(prev?: readonly unknown[], next?: readonly unknown[]): boolean {
  if (prev === next) return false
  if (!prev || !next || prev.length !== next.length) return true
  for (let i = 0; i < prev.length; i++) {
    if (!Object.is(prev[i], next[i])) return true
  }
  return false
}
